import React, { useState, useEffect } from 'react';
import { useSendTransaction, useAccount } from 'wagmi';
import { parseEther } from 'viem';
import toast from 'react-hot-toast';
import Countdown from 'react-countdown';
import { imageUrl } from '../utils/constants.ts';

interface RequestDetails {
    _id: string; // or requestId
    requestId: string; // Ensure this is present from backend data
    streamerAddress: string;
    invitedAddress: string;
    entryPriceBNB: number;
    scheduleInfo: string;
    status: string;
    streamerInternalWallet: string;
    createdAt: string;
    streamerUsername?: string; // Added by backend notification formatting
    streamerProfilePicture?: string; // Added by backend notification formatting
}

interface Props {
    isOpen: boolean;
    onClose: () => void;
    requestDetails: RequestDetails | null;
    onPaymentSuccess: (txHash: string, requestId: string) => void;
    onRejectSuccess: (requestId: string) => void;
}

const PrivateStreamPaymentModal: React.FC<Props> = ({
    isOpen,
    onClose,
    requestDetails,
    onPaymentSuccess,
    onRejectSuccess,
}) => {
    const { address: connectedAddress } = useAccount();
    const { data: hash, isPending, isSuccess, isError, error, sendTransaction } = useSendTransaction();
    const [isProcessingBackend, setIsProcessingBackend] = useState(false);

    useEffect(() => {
        if (isOpen && requestDetails) {
            console.log("[PaymentModal] Opened with requestDetails:", requestDetails);
            // Make sure this has the streamerInternalWallet for payment destination
            console.log("[PaymentModal] Payment will go to:", requestDetails.streamerInternalWallet);
        }
    }, [isOpen, requestDetails]);

    useEffect(() => {
        if (isSuccess && hash && requestDetails) {
            console.log('[PaymentModal] Transaction successful:', hash);
            toast.success('Payment sent! Processing confirmation...');
            setIsProcessingBackend(true);
            onPaymentSuccess(hash, requestDetails.requestId);
        }
        if (isError && error) {
            console.error('[PaymentModal] Transaction error:', error);
            toast.error(`Payment failed: ${error.message.split('\n')[0]}`);
            setIsProcessingBackend(false);
        }
    }, [isSuccess, hash, isError, error, onPaymentSuccess, requestDetails]);

    useEffect(() => {
        if (isOpen) {
            setIsProcessingBackend(false);
        }
    }, [isOpen, requestDetails]);

    const handlePay = () => {
        console.log('[Modal] handlePay called.');
        console.log('[Modal] requestDetails:', requestDetails);
        console.log('[Modal] connectedAddress:', connectedAddress);
        console.log('[Modal] isPending:', isPending);
        console.log('[Modal] isProcessingBackend:', isProcessingBackend);

        if (!requestDetails || !connectedAddress || isPending || isProcessingBackend) {
            console.warn('[Modal] Payment blocked. Conditions not met.');
            return;
        }

        const paymentAmountBNB = requestDetails.entryPriceBNB.toString();
        // Make sure we're sending to the streamer's wallet
        const recipientAddress = requestDetails.streamerInternalWallet as `0x${string}`;
        const paymentValueWei = parseEther(paymentAmountBNB);

        console.log(`[Modal] Initiating payment... To: ${recipientAddress}, Amount (BNB): ${paymentAmountBNB}, Value (Wei): ${paymentValueWei.toString()}`);

        try {
            sendTransaction({
                to: recipientAddress,
                value: paymentValueWei,
            });
            console.log('[Modal] sendTransaction function called.');
        } catch (txError) {
             console.error('[Modal] Error directly calling sendTransaction:', txError);
             toast.error("Error initiating payment transaction.");
        }
    };

    const handleReject = async () => {
        if (!requestDetails || isProcessingBackend || isPending) return;
        console.log(`[PaymentModal] Rejecting request ID: ${requestDetails.requestId}`);
        setIsProcessingBackend(true);
        try {
             await onRejectSuccess(requestDetails.requestId);
             toast.success('Invitation rejected.');
        } catch (rejectError: any) {
             console.error('[PaymentModal] Error rejecting invitation:', rejectError);
             toast.error(`Failed to reject: ${rejectError.message || 'Server error'}`);
             setIsProcessingBackend(false);
        }
    };

    const getScheduleComponent = () => {
        if (!requestDetails?.scheduleInfo) return <p>Schedule: Not specified</p>;

        const date = new Date(requestDetails.scheduleInfo);
        if (!isNaN(date.getTime()) && date.getTime() > Date.now()) {
            return (
                <p>
                    Starts in: <Countdown date={date} />
                </p>
            );
        }

        const timeMatch = requestDetails.scheduleInfo.match(/(\d+)\s+(hour|minute)s?/i);
        if (timeMatch) {
            const value = parseInt(timeMatch[1], 10);
            const unit = timeMatch[2].toLowerCase();
            const startDate = new Date(requestDetails.createdAt);
            if (unit === 'hour') {
                startDate.setHours(startDate.getHours() + value);
            } else if (unit === 'minute') {
                startDate.setMinutes(startDate.getMinutes() + value);
            }

            if (startDate.getTime() > Date.now()) {
                 return (
                    <p>
                        Starts in approx: <Countdown date={startDate} /> (relative to invite time)
                    </p>
                );
            } else {
                 return <p>Schedule: {requestDetails.scheduleInfo} (Likely started)</p>;
            }
        }

        return <p>Schedule: {requestDetails.scheduleInfo}</p>;
    };

    if (!isOpen || !requestDetails) return null;

    const isLoading = isPending || isProcessingBackend;

    const displayUsername = requestDetails.streamerUsername || 'Unknown User';
    const displayProfilePic = requestDetails.streamerProfilePicture;
    const profilePicSrc = displayProfilePic && displayProfilePic !== 'profil1.jpg'
        ? `${imageUrl}profile/${displayProfilePic}`
        : null;

    // --- ADD LOG: Log the final image source URL ---
    console.log(`[PaymentModal] Displaying username: ${displayUsername}, Profile Pic Src: ${profilePicSrc}`);
    // --- END LOG ---

    return (
        <div style={styles.overlay}>
            <div style={styles.modal}>
                <h2>Private Stream Invitation</h2>
                <button onClick={onClose} style={styles.closeButton} disabled={isLoading}>×</button>

                <div style={styles.streamerInfo}>
                    {profilePicSrc ? (
                        <img
                            src={profilePicSrc}
                            alt={displayUsername}
                            style={styles.avatar}
                            onError={(e) => {
                                console.warn(`[Modal] Failed to load profile picture: ${profilePicSrc}`);
                                e.currentTarget.style.display = 'none';
                                const placeholder = e.currentTarget.nextElementSibling;
                                if (placeholder) placeholder.style.display = 'flex';
                            }}
                        />
                    ) : (
                         <div style={{...styles.avatar, backgroundColor: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                             <span style={{color: 'white', fontSize: '1.5rem'}}>{displayUsername.charAt(0).toUpperCase()}</span>
                         </div>
                    )}
                    {!profilePicSrc && <div style={{...styles.avatar, backgroundColor: '#555', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: '-65px'}}>
                             <span style={{color: 'white', fontSize: '1.5rem'}}>{displayUsername.charAt(0).toUpperCase()}</span>
                         </div>}
                    <div>
                        <p><strong>From:</strong> {displayUsername}</p>
                        <p style={styles.addressText}>({requestDetails.streamerAddress})</p>
                    </div>
                </div>

                <hr style={styles.hr} />

                <p><strong>Price:</strong> {requestDetails.entryPriceBNB} BNB</p>
                {getScheduleComponent()}

                <div style={styles.actions}>
                    <button
                        onClick={handlePay}
                        disabled={isLoading || !connectedAddress}
                        style={isLoading || !connectedAddress ? { ...styles.button, ...styles.payButton, ...styles.disabledButton } : { ...styles.button, ...styles.payButton }}
                    >
                        {isPending ? 'Check Wallet...' : isProcessingBackend ? 'Processing...' : 'Pay Ticket'}
                    </button>
                    <button
                        onClick={handleReject}
                        disabled={isLoading}
                        style={isLoading ? { ...styles.button, ...styles.rejectButton, ...styles.disabledButton } : { ...styles.button, ...styles.rejectButton }}
                    >
                        {isProcessingBackend ? 'Processing...' : 'Reject'}
                    </button>
                </div>

            </div>
        </div>
    );
};

const styles: { [key: string]: React.CSSProperties } = {
    overlay: {
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        zIndex: 1000,
    },
    modal: {
        backgroundColor: '#2a2a2a',
        color: '#e0e0e0',
        padding: '25px',
        borderRadius: '8px',
        width: '90%',
        maxWidth: '450px',
        position: 'relative',
        boxShadow: '0 5px 15px rgba(0,0,0,0.3)',
    },
    closeButton: {
        position: 'absolute',
        top: '10px',
        right: '15px',
        background: 'none',
        border: 'none',
        fontSize: '1.8rem',
        color: '#aaa',
        cursor: 'pointer',
    },
    streamerInfo: {
        display: 'flex',
        alignItems: 'center',
        marginBottom: '15px',
    },
    avatar: {
        width: '50px',
        height: '50px',
        borderRadius: '50%',
        marginRight: '15px',
        objectFit: 'cover',
        border: '1px solid #444',
    },
    addressText: {
        fontSize: '0.8em',
        color: '#aaa',
        wordBreak: 'break-all',
        marginTop: '-5px',
    },
    hr: {
        border: 0,
        height: '1px',
        backgroundColor: '#444',
        margin: '15px 0',
    },
    actions: {
        marginTop: '20px',
        display: 'flex',
        justifyContent: 'space-between',
    },
    button: {
        padding: '10px 18px',
        border: 'none',
        borderRadius: '5px',
        cursor: 'pointer',
        fontSize: '1rem',
        fontWeight: 'bold',
        transition: 'background-color 0.2s ease',
    },
    payButton: {
        backgroundColor: '#4CAF50',
        color: 'white',
    },
    rejectButton: {
        backgroundColor: '#f44336',
        color: 'white',
    },
    disabledButton: {
        opacity: 0.6,
        cursor: 'not-allowed',
    },
    txLink: {
        marginTop: '15px',
        fontSize: '0.9em',
        color: '#aaa',
        wordBreak: 'break-all',
    }
};

export default PrivateStreamPaymentModal;
